'use client';

import { useQuery } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { CrawlerLastFinishedMetaSchema } from '@terra/shared-contracts-applications';
import type { z } from 'zod';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { useProjectStore } from '../../store/projectStore';
import type { CrawlerCredentialLastMeta } from '../useCrawlerSessions';

type CrawlerLastFinishedMeta = z.infer<typeof CrawlerLastFinishedMetaSchema>;

type UseCrawlerLastFinishedByCredentialOptions = {
  applicationId: string | undefined;
  enabled?: boolean;
};

export const CRAWLER_LAST_FINISHED_BY_CREDENTIAL_QUERY_KEY =
  'crawler-last-finished-by-credential';

export function useCrawlerLastFinishedByCredential({
  applicationId,
  enabled = true,
}: UseCrawlerLastFinishedByCredentialOptions): {
  lastFinishedByCredential: Record<string, CrawlerCredentialLastMeta>;
  isLoading: boolean;
} {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  const query = useQuery({
    queryKey: [
      CRAWLER_LAST_FINISHED_BY_CREDENTIAL_QUERY_KEY,
      applicationId,
      tenantId,
    ],
    queryFn: async (): Promise<Record<string, CrawlerLastFinishedMeta>> => {
      if (!applicationId || !tenantId) {
        return {};
      }

      const authHeaders = await getAuthHeaders();
      const response =
        await applicationsClient.getCrawlerLastFinishedByCredential({
          params: { id: applicationId },
          overrideClientOptions: {
            baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
          },
          extraHeaders: {
            ...authHeaders,
          },
        });

      if (response.status === 200) {
        return response.body.lastFinishedByCredential;
      }

      throw new Error('Failed to fetch last finished crawler sessions');
    },
    enabled: !!applicationId && !!tenantId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return {
    lastFinishedByCredential: query.data ?? {},
    isLoading: query.isLoading,
  };
}
